import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useMemo,
} from "react";
import { Accept, useDropzone } from "react-dropzone";
import { acceptsCad, cadValidator } from "shared/constants/officeFiles";
import { Icons } from "assets";
import classNames from "classnames";
import CustomToolTip from "shared/components/common/customToolTip";
import SkeletonLoader from "shared/components/common/skeleton";
import FilterDropdown from "shared/components/common/filterDropdown";
import styles from "./style.module.scss";

interface UploadedFile {
  id: string;
  file: File;
  progress: number;
  preview?: string;
}

export type UploadSortValue =
  | "name_asc"
  | "name_desc"
  | "modified_asc"
  | "modified_desc";

const SORT_OPTIONS = [
  "Default Order",
  "Date Modified ↓",
  "Date Modified ↑",
  "Name A→Z",
  "Name Z→A",
];

const LABEL_TO_SORT: Record<string, UploadSortValue | null> = {
  "Default Order": null,
  "Date Modified ↓": "modified_desc",
  "Date Modified ↑": "modified_asc",
  "Name A→Z": "name_asc",
  "Name Z→A": "name_desc",
};

const sortToLabel = (value: UploadSortValue | null | undefined): string => {
  switch (value) {
    case "modified_desc":
      return "Date Modified ↓";
    case "modified_asc":
      return "Date Modified ↑";
    case "name_asc":
      return "Name A→Z";
    case "name_desc":
      return "Name Z→A";
    default:
      return "Default Order";
  }
};

interface CustomUploadProps {
  onFileChange?: (files: File[]) => void;
  onUploadComplete?: () => void;
  error?: string | null;
  onRemove?: (fileId: string) => void;
  accept?: Accept;
  initialPreview?: string;
  acceptImages?: boolean;
  acceptFiles?: boolean;
  maxSize?: number;
  externalProgress?: { [fileName: string]: number };
  isUploading?: boolean;
  syncFiles?: File[];
  existingFiles?: File[];
  getFileKey?: (file: File) => string;
  onDuplicateError?: (duplicateNames: string[]) => void;
  mediaSort?: UploadSortValue | null;
  onMediaSortChange?: (value: UploadSortValue | null) => void;
}

const CustomUpload = ({
  onFileChange,
  onUploadComplete,
  error,
  onRemove,
  accept,
  acceptImages = true,
  acceptFiles = true,
  maxSize = 50 * 1024 * 1024,
  externalProgress,
  isUploading = false,
  syncFiles,
  existingFiles = [],
  getFileKey,
  onDuplicateError,
  mediaSort,
  onMediaSortChange,
}: CustomUploadProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const uniqueId = useId();
  const hasCalledCompleteRef = useRef(false);

  const rejectCadUnlessAllowed = (file: File) =>
    cadValidator(getDefaultAccept() as Record<string, string[]>)(file);

  const getDefaultAccept = (): Accept => {
    if (accept) return accept;

    const acceptObj: Accept = {};

    if (acceptImages) {
      acceptObj["image/*"] = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
    }

    if (acceptFiles) {
      acceptObj["application/pdf"] = [".pdf"];
      acceptObj["application/msword"] = [".doc"];
      acceptObj[
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      ] = [".docx"];
      acceptObj["application/vnd.ms-excel"] = [".xls"];
      acceptObj[
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ] = [".xlsx"];
      acceptObj["text/*"] = [".txt", ".csv"];
    }

    if (!acceptImages && !acceptFiles) {
      return {};
    }

    return acceptObj;
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      let filesToProcess = acceptedFiles;
      if (existingFiles.length > 0 && getFileKey) {
        const existingFileKeys = new Set(existingFiles.map(getFileKey));
        const duplicateFiles: File[] = [];
        const uniqueFiles: File[] = [];

        acceptedFiles.forEach((file) => {
          const fileKey = getFileKey(file);
          if (existingFileKeys.has(fileKey)) {
            duplicateFiles.push(file);
          } else {
            uniqueFiles.push(file);
          }
        });

        if (duplicateFiles.length > 0) {
          const duplicateNames = duplicateFiles.map((f) => f.name);
          if (onDuplicateError) {
            onDuplicateError(duplicateNames);
          }
        }

        filesToProcess = uniqueFiles;
      }

      if (filesToProcess.length === 0) {
        return;
      }

      const newFiles: UploadedFile[] = [];

      filesToProcess.forEach((file) => {
        if (file.size > maxSize) {
          return;
        }

        const fileId = `${Date.now()}-${Math.random()}`;
        const previewUrl = file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : undefined;

        const newFile: UploadedFile = {
          id: fileId,
          file,
          progress: 0,
          preview: previewUrl,
        };

        newFiles.push(newFile);

        if (!externalProgress) {
          let progress = 0;
          const interval = setInterval(() => {
            progress += 10;
            setUploadedFiles((prev) => {
              const updated = prev.map((f) =>
                f.id === fileId
                  ? { ...f, progress: Math.min(progress, 100) }
                  : f,
              );

              if (progress >= 100) {
                clearInterval(interval);
              }

              return updated;
            });
          }, 200);
        }
      });

      if (newFiles.length > 0) {
        // Appended files make the combined order manual — back to Default.
        if (onMediaSortChange) {
          onMediaSortChange(null);
        }
        setUploadedFiles((prev) => {
          const updated = [...prev, ...newFiles];
          if (onFileChange) {
            const allFiles = updated.map((uf) => uf.file);
            onFileChange(allFiles);
          }
          return updated;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onFileChange, onUploadComplete, maxSize, externalProgress, onMediaSortChange],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
    open,
  } = useDropzone({
    onDrop,
    accept: getDefaultAccept(),
    multiple: true,
    maxSize: maxSize,
    noClick: false,
    validator: rejectCadUnlessAllowed,
  });

  // Revoke previews only on unmount. Revoking on every uploadedFiles change
  // kills blob URLs still referenced by the next state (progress updates map
  // to new arrays holding the same previews), breaking thumbnails that mount
  // after upload completes. Removal paths (handleRemove/syncFiles) revoke
  // their own previews.
  const uploadedFilesRef = useRef<UploadedFile[]>([]);
  useEffect(() => {
    uploadedFilesRef.current = uploadedFiles;
  }, [uploadedFiles]);
  useEffect(() => {
    return () => {
      uploadedFilesRef.current.forEach((uf) => {
        if (uf.preview) {
          URL.revokeObjectURL(uf.preview);
        }
      });
    };
  }, []);

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      const allComplete = uploadedFiles.every((f) => f.progress === 100);
      const hasIncomplete = uploadedFiles.some((f) => f.progress < 100);

      if (hasIncomplete) {
        hasCalledCompleteRef.current = false;
      }

      if (allComplete && !hasCalledCompleteRef.current && onUploadComplete) {
        hasCalledCompleteRef.current = true;
        onUploadComplete();
      }
    } else {
      hasCalledCompleteRef.current = false;
    }
  }, [uploadedFiles, onUploadComplete]);

  const handleRemove = (fileId: string) => {
    if (externalProgress) {
      const fileToRemove = uploadedFiles.find((f) => f.id === fileId);
      if (fileToRemove && onFileChange) {
        const updated = uploadedFiles.filter((f) => f.id !== fileId);
        onFileChange(updated.map((uf) => uf.file));
      }
      if (onRemove) {
        onRemove(fileId);
      }
      return;
    }

    setUploadedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === fileId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      const updated = prev.filter((f) => f.id !== fileId);
      if (onFileChange) {
        onFileChange(updated.map((uf) => uf.file));
      }
      return updated;
    });
    if (onRemove) {
      onRemove(fileId);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type === "image/png") {
      return <Icons.PngIcon />;
    } else if (file.type.startsWith("image/")) {
      return <Icons.JpgIcon />;
    } else if (file.type === "application/pdf") {
      return <Icons.PdfIcon />;
    } else {
      return <Icons.DwgIcon />;
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newFiles = [...uploadedFiles];
    const draggedFile = newFiles[draggedIndex];
    newFiles.splice(draggedIndex, 1);
    newFiles.splice(dropIndex, 0, draggedFile);

    setUploadedFiles(newFiles);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Manual reorder means the order is custom again — back to Default.
    if (onMediaSortChange) {
      onMediaSortChange(null);
    }

    if (onFileChange) {
      onFileChange(newFiles.map((uf) => uf.file));
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleSortSelect = (label: string) => {
    const value = LABEL_TO_SORT[label] ?? null;
    if (onMediaSortChange) {
      onMediaSortChange(value);
    }
    // Default Order = keep the current manual order; nothing to reorder.
    if (!value) return;
    setUploadedFiles((prev) => {
      const sorted = [...prev].sort((a, b) => {
        switch (value) {
          case "name_asc":
            return a.file.name.localeCompare(b.file.name, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          case "name_desc":
            return b.file.name.localeCompare(a.file.name, undefined, {
              numeric: true,
              sensitivity: "base",
            });
          case "modified_asc":
            return a.file.lastModified - b.file.lastModified;
          case "modified_desc":
          default:
            return b.file.lastModified - a.file.lastModified;
        }
      });
      if (onFileChange) {
        onFileChange(sorted.map((uf) => uf.file));
      }
      return sorted;
    });
  };

  const externalProgressString = useMemo(() => {
    return externalProgress ? JSON.stringify(externalProgress) : "";
  }, [externalProgress]);

  useEffect(() => {
    if (syncFiles && externalProgress) {
      setUploadedFiles((prev) => {
        const syncFileKeys = new Set(
          syncFiles.map((f) => `${f.name}-${f.size}-${f.lastModified}`),
        );

        const updated = prev.filter((uf) => {
          const fileKey = `${uf.file.name}-${uf.file.size}-${uf.file.lastModified}`;
          return syncFileKeys.has(fileKey);
        });

        prev.forEach((uf) => {
          const fileKey = `${uf.file.name}-${uf.file.size}-${uf.file.lastModified}`;
          if (!syncFileKeys.has(fileKey) && uf.preview) {
            URL.revokeObjectURL(uf.preview);
          }
        });

        return updated;
      });
    }
  }, [syncFiles, externalProgress]);

  useEffect(() => {
    if (externalProgress) {
      setUploadedFiles((prev) =>
        prev.map((f) => {
          const externalProg = externalProgress[f.file.name];
          if (externalProg !== undefined && externalProg === 100) {
            return { ...f, progress: 100 };
          }
          return f;
        }),
      );
    }
  }, [externalProgressString]); // eslint-disable-line react-hooks/exhaustive-deps

  const filesWithProgress = useMemo(() => {
    return uploadedFiles.map((f) => {
      if (externalProgress && externalProgress[f.file.name] !== undefined) {
        return { ...f, progress: externalProgress[f.file.name] };
      }
      return f;
    });
  }, [uploadedFiles, externalProgressString]); // eslint-disable-line react-hooks/exhaustive-deps

  const completedCount = filesWithProgress.filter(
    (f) => f.progress === 100,
  ).length;

  return (
    <div className={styles.uploadContainer}>
      <div
        className={classNames(
          styles.uploadArea,
          isDragActive && styles.dragActive,
          isDragReject && styles.dragReject,
        )}
        {...getRootProps()}
      >
        <input id={`fileInput-${uniqueId}`} {...getInputProps()} />
        <div className={styles.uploadPlaceholder}>
          <Icons.ImgUploadIcon />
          <p>Drag and drop files here, or click to browse</p>
          <span>
            {acceptsCad(getDefaultAccept() as Record<string, string[]>)
              ? "JPG, PNG, PDF, DWG, DXF, DWF files supported"
              : "JPG, PNG, PDF files supported"}
          </span>
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <div className={styles.uploadedFilesSection}>
          <div className={styles.uploadedFilesHeader}>
            <h3 className="">Uploaded Files</h3>
            <div className="flex items-center gap-3">
              {onMediaSortChange && (
                <div
                  className="flex items-center gap-2"
                  style={
                    isUploading
                      ? { pointerEvents: "none", opacity: 0.5 }
                      : undefined
                  }
                >
                  <span className={styles.sortLabel}>Sort:</span>
                  <FilterDropdown
                    label=""
                    options={SORT_OPTIONS}
                    selected={sortToLabel(mediaSort)}
                    menuWidth="fit-content"
                    maxHeight="200px"
                    menuPosition="right"
                    onSelect={handleSortSelect}
                  />
                </div>
              )}
              <span>
                {completedCount} of {uploadedFiles.length} Complete
              </span>
            </div>
          </div>
          <div className={styles.filesGrid}>
            {filesWithProgress.map((uploadedFile, index) => {
              const isComplete = uploadedFile.progress === 100;
              const isImage =
                uploadedFile.file.type.startsWith("image/") &&
                !!uploadedFile.preview;
              return (
                <div
                  key={uploadedFile.id}
                  className={classNames(
                    styles.fileTile,
                    draggedIndex === index && styles.dragging,
                    dragOverIndex === index && styles.dragOver,
                  )}
                  draggable={isComplete}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >
                  <CustomToolTip
                    label={uploadedFile.file.name}
                    customTooltipTextStyle={styles.tileTooltip}
                  >
                    <div
                      className={classNames(
                        styles.tileFrame,
                        !isComplete && styles.uploading,
                      )}
                      style={
                        !isComplete
                          ? {
                              background: `conic-gradient(#2247c0 ${
                                uploadedFile.progress * 3.6
                              }deg, #e9ecef ${uploadedFile.progress * 3.6}deg)`,
                            }
                          : undefined
                      }
                    >
                      <div
                        className={classNames(
                          styles.tileBox,
                          isImage ? styles.imageBox : styles.fileBox,
                          "relative",
                        )}
                      >
                        {isImage ? (
                          // Mounted from the start so the browser decodes the
                          // blob during upload; the skeleton overlays it until
                          // complete, avoiding a decode flash on reveal.
                          <img
                            src={uploadedFile.preview}
                            alt={uploadedFile.file.name}
                            draggable={false}
                          />
                        ) : isComplete ? (
                          getFileIcon(uploadedFile.file)
                        ) : null}
                        {!isComplete && (
                          <SkeletonLoader className={styles.tileSkeleton} />
                        )}
                        <button
                          type="button"
                          aria-label="Delete file"
                          className={classNames(
                            styles.deleteIcon,
                            "absolute top-2 right-2 flex items-center justify-center",
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove(uploadedFile.id);
                          }}
                        >
                          <Icons.TrashOutline2 width={15} height={15} />
                        </button>
                      </div>
                    </div>
                    <span className={styles.tileName}>
                      {uploadedFile.file.name}
                    </span>
                    <span className={styles.tileSize}>
                      {formatFileSize(uploadedFile.file.size)}
                    </span>
                  </CustomToolTip>
                </div>
              );
            })}
            <button
              type="button"
              aria-label="Add more files"
              className={styles.addFileBox}
              onClick={open}
              disabled={isUploading}
            >
              <span className={styles.addFilePlus}>+</span>
            </button>
          </div>
        </div>
      )}

      <div className="flex items-start w-full justify-start">
        {fileRejections.length > 0 && (
          <p className={"error text-left"}>UnSupported file format</p>
        )}
        {error && <p className={"error text-start"}>{error}</p>}
      </div>
    </div>
  );
};

export default CustomUpload;
